import { GraphQLResult, generateClient } from "aws-amplify/api";
import { FieldContinuationDataForResolver, FieldTemplate, FieldTemplateForResolver } from "db/definitions";
import { bulkGetFieldContinuationDatas } from "db/queries/fieldContinuationDataQueries";
import { bulkRetrieveFieldTemplatesByFormTemplate, createFieldTemplateForBatchResolver } from "db/queries/fieldTemplateQueries";
// @ts-ignore
import { getFieldTemplatesByFormTemplate } from "graphql/queries";
import { recordSentry } from "utils/recordSentry";

interface FieldTemplateWithContData extends FieldTemplateForResolver {
	continuationData?: FieldContinuationDataForResolver;
}

interface QueryResponse {
	fieldTemplatesByFormTemplate: {
		items: FieldTemplateWithContData[];
		nextToken?: string;
	};
}

/**
 * Given the formTemplate id and whether or not we're online, retrieves the fieldTemplates and returns them
 * @author Eric Webb <ewebb@factorearth.com>
 * @param id The formTemplate id we want to fetch the fieldTemplates by
 * @param online Whether or not we're online
 * @returns The array of fieldTemplates retrieved
 */
async function batchGetFieldTemplatesByFormTemplate(
	id: string,
	online: boolean,
): Promise<{ fieldTemplates: FieldTemplateForResolver[]; fieldContDatas: FieldContinuationDataForResolver[] }> {
	if (id) {
		try {
			if (online) {
				let token = undefined;
				const fieldTemplates: FieldTemplateForResolver[] = [];
				const fieldContDatas: FieldContinuationDataForResolver[] = [];
				while (token !== null) {
					const client = generateClient({ authMode: "userPool" });
					const result = (await client.graphql({
						query: getFieldTemplatesByFormTemplate,
						authMode: "userPool",
						variables: {
							formTemplate: id,
							nextToken: token,
						},
					})) as GraphQLResult<QueryResponse>;
					const fieldTemplatesFromResponse = result.data.fieldTemplatesByFormTemplate.items;
					for (const fieldTemplate of fieldTemplatesFromResponse) {
						if (fieldTemplate.continuationData) fieldContDatas.push(fieldTemplate.continuationData);
						const parsedFieldTemplate = createFieldTemplateForBatchResolver(fieldTemplate);
						fieldTemplates.push(parsedFieldTemplate);
					}
					token = result.data.fieldTemplatesByFormTemplate.nextToken || null;
				}
				return { fieldTemplates, fieldContDatas };
			} else {
				const fieldTemplates = await bulkRetrieveFieldTemplatesByFormTemplate(id);
				const contIds: string[] = [];
				for (const fieldTemplate of fieldTemplates) {
					if (fieldTemplate.continuationDataId) {
						contIds.push(fieldTemplate.continuationDataId);
					}
				}
				const fieldContDatas = await bulkGetFieldContinuationDatas(contIds);
				return { fieldTemplates, fieldContDatas };
			}
		} catch (err) {
			recordSentry({ error: err, msg: `ERROR GETTING FIELD TEMPLATE ${online ? "ONLINE" : "OFFLINE"}` });
		}
	}
	return { fieldTemplates: [], fieldContDatas: [] };
}

export default batchGetFieldTemplatesByFormTemplate;
